import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken, formatQuantity } from '../../utils';
import DecimalField from '../shared/DecimalField';
import { DEFAULT_VAT_RATE, calcPriceExclVat } from '../../utils/vat';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

function StockReceiveDialog({ t, open, onClose, inventoryItem, onSuccess }) {
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [priceIncludesVat, setPriceIncludesVat] = useState(orgDefaultVat);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setQuantity('');
    setUnitCost('');
    setPriceIncludesVat(orgDefaultVat);
    setReferenceNumber('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError(t('Please enter a valid quantity'));
      return;
    }

    // Always send VAT-exclusive price to backend
    let costExclVat = unitCost || null;
    if (costExclVat && priceIncludesVat) {
      costExclVat = calcPriceExclVat(parseFloat(costExclVat), DEFAULT_VAT_RATE);
    }

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        `/api/inventory/${inventoryItem.id}/receive/?organization=${currentOrganization.organization_id}`,
        {
          quantity: quantity,
          unit_cost: costExclVat,
          reference_number: referenceNumber,
          notes: notes,
        },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Failed to receive stock:', err);
      setError(err.response?.data?.error || t('Failed to receive stock'));
    } finally {
      setLoading(false);
    }
  };

  if (!inventoryItem) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="stock-receive-dialog">
      <DialogTitle>{t('inventory.receiveStock')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {inventoryItem.part_name}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('inventory.systemQuantity')}: <strong>{formatQuantity(inventoryItem.quantity_in_stock)}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <DecimalField
            label={t('inventory.quantity')}
            value={quantity}
            onChange={setQuantity}
            required
            autoFocus
            fullWidth
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DecimalField
                label={priceIncludesVat ? t('inventory.unitCostInclVat') : t('inventory.unitCostExclVat')}
                value={unitCost}
                onChange={setUnitCost}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
              />
              <VatModeToggle
                value={priceIncludesVat ? 'incl' : 'excl'}
                onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                sx={{ flexShrink: 0 }}
              />
            </Box>
            <VatConverseLine value={unitCost} mode={priceIncludesVat ? 'incl' : 'excl'} vatRate={DEFAULT_VAT_RATE} />
          </Box>

          <TextField
            label={t('inventory.referenceNumber')}
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText={t('inventory.referenceNumberHelp')}
          />

          <TextField
            label={t('inventory.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !quantity}
          data-testid="stock-receive-submit"
        >
          {loading ? <CircularProgress size={20} /> : t('inventory.receiveStock')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(StockReceiveDialog);
